import React, { useState, useEffect } from "react";
import axios from "axios";
import { PlusCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";

/* ── shared input style — matches AdminAttendanceOverview form inputs ─────── */
const inputStyle =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors";

/* ════════════════════════════════════════════════════════════════════════════ */
const LeaveModal = ({
  leaveData,
  setLeaveData,
  today,
  isLoading,
  setShowLeaveModal,
}) => {
  const employee = JSON.parse(localStorage.getItem("employee"));
  console.log("Emp", employee);

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveStatus, setLeaveStatus] = useState("");
  const [listData, setListData] = useState([]);

  useEffect(() => {
    console.log('leave data', leaveData);
    const fetchLeaveTypes = async () => {
      try {
        const response = await axios.get(`${SERVER_URL}/Rahul/get_leave_types.php`);
        setLeaveTypes(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching leave types:", error);
      }
    };
    fetchLeaveTypes();
  }, []);

  const calculateLeaveDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end   = new Date(endDate);
    let totalDays = 0;
    let list = [];

    const isThirdSaturday = (date) => {
      const day     = date.getDay();
      const dateNum = date.getDate();
      return day === 6 && dateNum >= 15 && dateNum <= 21;
    };

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const day = d.getDay();
      if (day === 0 || isThirdSaturday(d)) list.push(0);
      else list.push(1);
    }

    totalDays = list.reduce((a, b) => a + b, 0);
    return { totalDays, list };
  };

  const handleSubmitLeave = async () => {
    if (!leaveData.leave_type_id || !leaveData.start_date || !leaveData.end_date) {
      alert("Please fill all required fields.");
      return;
    }
    const { totalDays, list } = calculateLeaveDays(leaveData.start_date, leaveData.end_date);
    const payload = {
      employee_id:   employee.employee_id,
      leave_type_id: leaveData.leave_type_id,
      start_date:    leaveData.start_date,
      end_date:      leaveData.end_date,
      reason:        leaveData.reason || "",
      total_days:    totalDays,
      countList:     list,
    };

    try {
      const response = await axios.post(`${SERVER_URL}/Rahul/submit_leave.php`, payload);
      setLeaveStatus(response.data.message || "Leave application submitted successfully!");
      setLeaveData({ leave_type_id: "", start_date: "", end_date: "", reason: "" });
    } catch (e) {
      console.log(e);
      setLeaveStatus("Something went wrong.");
    }
  };

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)" }}
    >
      <div
        className="w-full max-w-lg rounded-xl bg-white border border-slate-200 overflow-hidden"
        style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
      >
        {/* Header */}
        <div
          className="flex justify-between items-center px-6 py-4 border-b border-slate-200"
          style={{ background: "#f8fafc" }}
        >
          <div className="flex items-center gap-2.5">
            <div
              className="w-1 h-5 rounded-full"
              style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
            />
            <PlusCircleIcon className="w-4 h-4 text-blue-700" />
            <h3 className="text-sm font-bold text-slate-800 m-0 tracking-tight">
              Apply for Leave
            </h3>
          </div>
          <button
            onClick={() => setShowLeaveModal(false)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={(e) => { e.preventDefault(); handleSubmitLeave(); }} className="p-6 flex flex-col gap-4">

          {/* Leave Type */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Type of Leave
            </label>
            <select
              value={leaveData.leave_type_id}
              required
              onChange={(e) => setLeaveData({ ...leaveData, leave_type_id: e.target.value })}
              className={inputStyle}
              style={{ appearance: "auto" }}
            >
              <option value="">Select Leave Type</option>
              {leaveTypes.map((type) => (
                <option key={type.leave_type_id} value={type.leave_type_id}>
                  {type.leave_name}
                </option>
              ))}
            </select>
          </div>

          {/* Start / End Date row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Start Date
              </label>
              <input
                type="date"
                value={leaveData.start_date}
                min={today}
                required
                onChange={(e) => setLeaveData({ ...leaveData, start_date: e.target.value })}
                className={inputStyle}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                End Date
              </label>
              <input
                type="date"
                value={leaveData.end_date}
                min={leaveData.start_date}
                required
                onChange={(e) => setLeaveData({ ...leaveData, end_date: e.target.value })}
                className={inputStyle}
              />
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Reason <span className="normal-case text-slate-400 font-normal">(Optional)</span>
            </label>
            <textarea
              rows="3"
              placeholder="Reason (optional)"
              value={leaveData.reason}
              onChange={(e) => setLeaveData({ ...leaveData, reason: e.target.value })}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors resize-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-200 mt-1">
            <button
              type="button"
              onClick={() => setShowLeaveModal(false)}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
            >
              {isLoading ? "Submitting…" : "Submit Application"}
            </button>
          </div>

          {/* Status Message */}
          {leaveStatus && (
            <p
              className={`text-center text-sm font-medium px-4 py-2 rounded-lg ${
                leaveStatus.toLowerCase().includes("success")
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-rose-50 text-rose-700 border border-rose-200"
              }`}
            >
              {leaveStatus}
            </p>
          )}

        </form>
      </div>
    </div>
  );
};

export default LeaveModal;